import { useCallback, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useCustomer } from '@order/Customer';
import {
  useCart,
  useInAppReview,
  useLocalOrderHistory,
  useOrderFeedbackNotifications,
  usePermanentNoticeBanner,
} from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useHasOrdersInCache } from '@order/Order';
import { usePlacedOrdersCounter } from '@order/PlacedOrdersCounter';

import type { ModalStackParamList } from '../../../../navigation/AppNavigation.props';
import type { CheckoutPayload } from '../../Checkout.utils';
import { triggerCheckoutHapticFeedback } from '../../Checkout.utils';
import { useCheckoutBrazeLogPurchase } from '../useCheckoutBrazeLogPurchase';
import type { useCheckoutLedger } from '../useCheckoutLedger';
import { useCheckoutNoticeBanners } from '../useCheckoutNoticeBanners';
import { useCheckoutTelemetryEvents } from '../useCheckoutTelemetry';
import { usePostOrderSubmitCallback } from '../usePostOrderSubmitCallback';
import { useSubmitOrderMutation } from '../useSubmitOrderMutation';
import { useValidateRewardException } from '../useValidateRewardException';

export const useSubmitOrder = (params: UseSubmitOrderParams) => {
  const { cartData, ledger, payload } = params;

  const navigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  const { scheduleOrderFeedbackNotification } = useOrderFeedbackNotifications();
  const { incrementPlacedOrdersCounter } = usePlacedOrdersCounter();
  const logBrazePurchase = useCheckoutBrazeLogPurchase({
    orderDetails: cartData,
    orderLedger: ledger,
  });

  // ───── Mutation ─────────────────────────────────────────────────
  const { isSubmittingOrder, rewardFailureCode, submitOrderMutation } =
    useSubmitOrderMutation(payload);

  // ───── Reward Exception ─────────────────────────────────────────
  const validateRewardException = useValidateRewardException(rewardFailureCode);

  // ───── Cart Data ────────────────────────────────────────────────
  const { minimumDeliveryAmount, fetchCart, ...telemetryCart } = useCartData();

  // ───── Telemetry ────────────────────────────────────────────────
  const { customer } = useCustomer();
  const { hasOrdersInCache } = useHasOrdersInCache();
  const telemetry = useCheckoutTelemetryEvents({
    customer,
    ...telemetryCart,
    payload,
    hasPreviousOrders: hasOrdersInCache,
  });

  // ───── Notice Banners ───────────────────────────────────────────
  const noticeBanners = useCheckoutNoticeBanners();

  // ───── In App Review ────────────────────────────────────────────
  const requestInAppReview = useInAppReview();

  // ───── Local Order History ──────────────────────────────────────
  const { addToLocalOrderHistory } = useLocalOrderHistory();

  // ───── Placing Order Notice ─────────────────────────────────────
  const dismissPlacingOrderNotice = usePlacingOrderNotice(isSubmittingOrder);

  // ───── Handling Post-Order Requests ─────────────────────────────
  const handlePostOrderSubmit = usePostOrderSubmitCallback();

  // ───── Return Callback ──────────────────────────────────────────
  const submitOrder = useCallback(
    async ({ input, handleInvalidWantedTime }: SubmitOrderProps) => {
      const { orderId, wantedTime } = input;

      // ───── Started Submit ───────────────────────────────────────
      telemetry.checkoutStart();

      // ───── Submit Request ───────────────────────────────────────
      const { isSuccess, typename, userError, systemError } =
        await submitOrderMutation(input);

      // ───── Haptic Feedback ──────────────────────────────────────
      void triggerCheckoutHapticFeedback(isSuccess);

      // ───── Dismiss Loading Feedback ─────────────────────────────
      dismissPlacingOrderNotice();

      // ───── Checkout Failed - Reward Not Validated ───────────────
      if (typename === 'RewardNotValidated') {
        fetchCart(); // Refresh session to get updated rewards.

        return;
      }

      // ───── Checkout Failed - Timeslot Unavailable ───────────────
      if (typename === 'TimeslotUnavailable') {
        telemetry.timeslotUnavailable(systemError);
        handleInvalidWantedTime(wantedTime);

        return;
      }

      // ───── Checkout Failed - Minimum Delivery Not Met ───────────
      if (typename === 'MinimumDeliverySubtotalNotMet') {
        telemetry.deliveryMinimumNotMet(systemError, minimumDeliveryAmount);
        noticeBanners.deliveryMinimumNotMet(minimumDeliveryAmount);

        return;
      }

      // ───── Checkout Failed - Product Unavailable ───────────
      if (typename === 'ProductUnavailable') {
        telemetry.productUnavailable(systemError, userError);
        noticeBanners.productUnavailable(userError);

        return;
      }

      // ───── Checkout Failed - Restaurant Is Not Accepting Orders ───────────
      if (typename === 'RestaurantIsNotAcceptingOrders') {
        telemetry.restaurantIsNotAcceptingOrders(systemError, userError);
        noticeBanners.restaurantIsNotAcceptingOrders(userError);

        return;
      }

      // ───── Checkout Failed - Invalid tip ────────────────────────
      if (typename === 'InvalidTip') {
        telemetry.invalidTip(userError);
        noticeBanners.otherKnownErrors(typename);

        return;
      }

      // ───── Checkout Failed - Other Known Errors ─────────────────
      if (typename && KNOWN_CHECKOUT_ISSUES.has(typename)) {
        telemetry.otherKnownErrors(systemError, typename);
        noticeBanners.otherKnownErrors(typename);

        return;
      }

      // ───── Checkout Failed - Unknown Error ──────────────────────
      if (!isSuccess) {
        telemetry.unknownCheckoutError(systemError);
        noticeBanners.unknownCheckoutError();

        return;
      }

      // ───── Checkout Success ─────────────────────────────────────
      handlePostOrderSubmit();
      void scheduleOrderFeedbackNotification({ orderId, wantedTime });
      incrementPlacedOrdersCounter();
      navigation.replace('OrderStatus', { orderId });
      telemetry.checkoutSuccess();
      requestInAppReview();
      addToLocalOrderHistory({ orderId });
      logBrazePurchase();
    },
    [
      navigation,
      minimumDeliveryAmount,
      telemetry,
      noticeBanners,
      submitOrderMutation,
      requestInAppReview,
      addToLocalOrderHistory,
      dismissPlacingOrderNotice,
      fetchCart,
      scheduleOrderFeedbackNotification,
      handlePostOrderSubmit,
      incrementPlacedOrdersCounter,
      logBrazePurchase,
    ],
  );

  // ───── Result ───────────────────────────────────────────────────
  return { isSubmittingOrder, validateRewardException, submitOrder };
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

const useCartData = () => {
  const { cart, cartOrderType, total, deliveryDetails, fetchCart } = useCart();
  const { formatPrice } = useLocalizationContext();

  const minimumDeliveryAmount = formatPrice(
    deliveryDetails?.minSubtotal ?? 0,
    'USD',
  );

  return useMemo(
    () => ({ cart, cartOrderType, total, minimumDeliveryAmount, fetchCart }),
    [cart, cartOrderType, total, minimumDeliveryAmount, fetchCart],
  );
};

const usePlacingOrderNotice = (placingOrder: boolean) => {
  const { t } = useLocalizationContext();
  const text = t('checkout.placing-order-banner');

  const { dismiss } = usePermanentNoticeBanner(
    placingOrder,
    text,
    'placing-order-notice',
  );

  return useCallback(() => {
    dismiss('placing-order-notice');
  }, [dismiss]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const KNOWN_CHECKOUT_ISSUES = new Set([
  'InvalidBillingAccount',
  'InvalidCustomerPhone',
  'DeliveryAlreadyInFlight',
  'DeliverySpecifyAddress',
]);

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSubmitOrderParams = Readonly<{
  payload: CheckoutPayload;
  cartData: ReturnType<typeof useCart>;
  ledger: ReturnType<typeof useCheckoutLedger>['ledger'];
}>;

type SubmitOrderProps = Readonly<{
  input: Parameters<
    ReturnType<typeof useSubmitOrderMutation>['submitOrderMutation']
  >['0'];
  handleInvalidWantedTime: (wantedTime?: string) => void;
}>;
